import '../../modal/components/modal/modal.component.scss';
import './membership-modal-extensions.scss';

import type { FunctionComponent } from 'preact';
import { useCallback, useMemo } from 'preact/hooks';

import { useTranslation } from '../../i18n';
import type { InteractionType, Membership, Tier } from '../models/membership.model';
import { formatDate, getMembershipTermName } from '../utils/membership.formatters';

export interface MembershipConfirmChangeModalProps {
  membership: Membership;
  tier: Tier;
  termId: string;
  interactionType: InteractionType;
  onClose: (selectedTermId: string | null) => void;
}

export const MembershipConfirmChangeModal: FunctionComponent<MembershipConfirmChangeModalProps> = ({
  membership,
  tier,
  termId,
  interactionType,
  onClose,
}) => {
  const { t } = useTranslation();

  const selectedTerm = useMemo(
    () => tier.terms?.find((term) => term.id === termId),
    [tier.terms, termId]
  );

  const modalTitle = useMemo(
    () =>
      interactionType === 'UPGRADE'
        ? t('membership.confirmUpgrade')
        : t('membership.confirmDowngrade'),
    [interactionType, t]
  );

  const message = useMemo(
    () =>
      interactionType === 'UPGRADE'
        ? t('membership.aboutToUpgrade')
        : t('membership.aboutToDowngrade'),
    [interactionType, t]
  );

  const handleConfirm = useCallback(() => {
    onClose(termId);
  }, [termId, onClose]);

  const handleCancel = useCallback(() => {
    onClose(null);
  }, [onClose]);

  return (
    <div class="single-membership-confirm-change-modal">
      {modalTitle && (
        <div class="single-modal-header">
          <h3 class="single-modal-title">{modalTitle}</h3>
        </div>
      )}
      <div class="single-modal-body">
        <p class="message" dangerouslySetInnerHTML={{ __html: message }} />
        {selectedTerm && (
          <p>
            <span class="bold">{t('membership.newTierTerm')}</span>{' '}
            {getMembershipTermName(selectedTerm)}
          </p>
        )}
        {membership.nextBillingDate && (
          <p>
            <span class="bold">{t('membership.nextBillingDateLabel')}</span>{' '}
            {formatDate(membership.nextBillingDate)}
          </p>
        )}
      </div>
      <div class="single-modal-footer single-action-list inline--btns">
        <button className="btn btn-outline button" onClick={handleCancel} type="button">
          {t('common.cancel')}
        </button>
        <button
          className="btn button"
          onClick={handleConfirm}
          disabled={!termId || termId === membership.selectedTerm?.id}
        >
          {t('membership.confirm')}
        </button>
      </div>
    </div>
  );
};
